import { WrapperWidget } from '@agentselly/ui';
import { useTranslate, T } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { EmptyState } from '~/components/empty/EmptyState';
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

interface Advisor {
  readonly name: string;
  readonly role: string;
  readonly phone?: string;
  readonly email?: string;
}

interface Props {
  readonly hubspotMeetingLink: string | null;
  /** Internal /book page; preferred over the HubSpot link when set. */
  readonly bookUrl?: string | null;
  readonly advisor?: Advisor | null;
  readonly v3Redesign?: boolean;
}

export function ContactPhoneWidget(props: Props) {
  const { t } = useTranslate();
  const bookingHref = props.bookUrl || props.hubspotMeetingLink;

  if (!props.v3Redesign) {
    if (!bookingHref) {
      return (
        <WrapperWidget>
          <EmptyState
            title={<T>contact_phone_widget_title</T>}
            description={<T>contact_phone_widget_text</T>}
          />
        </WrapperWidget>
      );
    }

    return (
      <WrapperWidget>
        <div className="h-full flex flex-col gap-y-6">
          <h2 className="text-lg font-bold">
            <T>contact_phone_widget_title</T>
          </h2>
          <p>
            <T>contact_phone_widget_text</T>
          </p>

          <a
            href={bookingHref}
            {...(props.bookUrl ? {} : { target: '_blank', rel: 'noreferrer' })}
            className="inline-block"
          >
            <Button
              primary
              showArrow
            >
              <T>contact_phone_widget_button_label</T>
            </Button>
          </a>
        </div>
      </WrapperWidget>
    );
  }

  const advisor = props.advisor;

  const initials = advisor?.name
    ? advisor.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join('')
    : 'AS';

  return (
    <WrapperWidget>
      <div className="v3-stack h-full justify-between">
        <div>
          <h2 className="text-lg font-bold text-[var(--v3-tx)] mb-2">
            {t('contact_phone_widget_title', 'Ihr persönlicher Berater')}
          </h2>
          <p className="text-sm text-[var(--v3-mu)] mb-6">
            {t(
              'contact_phone_widget_text',
              'Vereinbaren Sie direkt einen Online-Termin oder kontaktieren Sie Ihren Berater telefonisch oder per E-Mail.',
            )}
          </p>

          {advisor ? (
            <div className="v3-card bg-[var(--v3-neubg)] border-[var(--v3-bd)] p-5 mb-6">
              {/* Advisor identity */}
              <div className="v3-adv-who mb-4">
                <div className="v3-av4">{initials}</div>
                <div className="min-w-0">
                  <h3 className="font-bold text-base text-[var(--v3-tx)] truncate">
                    {advisor.name}
                  </h3>
                  <p className="text-xs text-[var(--v3-mu)] truncate">
                    {advisor.role ||
                      t(
                        'contact_phone_widget_default_role',
                        'Immobilienberater',
                      )}
                  </p>
                </div>
              </div>

              {/* Contact methods */}
              <div className="flex flex-col gap-3 pt-3 border-t border-[var(--v3-bd)] text-sm">
                {advisor.phone ? (
                  <div className="v3-adv-ln">
                    <div className="v3-adv-ln-icon">
                      <PhoneIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <small>
                        {t('contact_phone_widget_phone_label', 'Telefon')}
                      </small>
                      <a href={`tel:${advisor.phone}`}>{advisor.phone}</a>
                    </div>
                  </div>
                ) : null}

                {advisor.email ? (
                  <div className="v3-adv-ln">
                    <div className="v3-adv-ln-icon">
                      <EnvelopeIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <small>
                        {t('contact_phone_widget_email_label', 'E-Mail')}
                      </small>
                      <a href={`mailto:${advisor.email}`}>{advisor.email}</a>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="v3-callout warn mb-6">
              <div>
                <p className="font-semibold text-xs mb-1">
                  {t(
                    'contact_phone_widget_no_advisor_title',
                    'Kein persönlicher Berater zugewiesen',
                  )}
                </p>
                <p className="text-xs">
                  {t(
                    'contact_phone_widget_no_advisor_text',
                    'Aktuell ist Ihrem Dossier noch kein dedizierter Berater zugewiesen. Bitte senden Sie uns eine Nachricht über das Formular — unser Team meldet sich umgehend bei Ihnen.',
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Meeting Booking Action Button */}
        <div>
          {advisor && bookingHref ? (
            <a
              href={bookingHref}
              {...(props.bookUrl
                ? {}
                : { target: '_blank', rel: 'noreferrer' })}
              className="v3-btn pri w-full py-3 text-center"
            >
              <CalendarIcon className="w-5 h-5" />
              <span>
                {t('contact_phone_widget_button_label', 'Termin vereinbaren')}
                {!props.bookUrl ? ' ↗' : ''}
              </span>
            </a>
          ) : (
            <div>
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="v3-btn pri w-full py-3 text-center opacity-50 cursor-not-allowed"
              >
                <CalendarIcon className="w-5 h-5" />
                <span>
                  {t('contact_phone_widget_button_label', 'Termin vereinbaren')}
                </span>
              </button>
              {!advisor && (
                <p className="text-xs text-[var(--v3-mu)] text-center mt-2">
                  {t(
                    'contact_phone_widget_no_advisor_hint',
                    'Terminbuchung erst nach Beraterzuweisung möglich.',
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </WrapperWidget>
  );
}
